import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { trpcClient } from '@/lib/trpc';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Loader2, Tags, Layers, Clock, TrendingUp, Settings, Sparkles } from 'lucide-react';
import { z } from 'zod';
import { generateProductTypeCode } from '@/utils/entityCodeGenerator';

export const productTypeSchema = z.object({
  code: z.string().min(1, 'Vui lòng nhập mã loại sản phẩm').max(50, 'Vui lòng nhập dưới 50 ký tự (Mã loại sản phẩm không được vượt quá 50 ký tự)'),
  category: z.string().optional(),
  group: z.string().optional(),
  unit: z.string().optional(),
  standardProcess: z.string().optional(),
  productionDays: z.number().min(0).optional(),
  profitMargin: z.string().optional(),
});

export type ProductTypeFormData = z.infer<typeof productTypeSchema>;

export interface ProductTypeFormProps {
  id?: string;
  isModal?: boolean;
  onSuccess?: (newItem: { id: string; label: string; code?: string; original?: any }) => void;
  onCancel?: () => void;
  initialData?: any;
  lockedFields?: Record<string, any>;
  submitLabel?: string;
}

export function ProductTypeForm({
  id: propId,
  isModal = false,
  onSuccess,
  onCancel,
  initialData: propInitialData,
  lockedFields = {},
  submitLabel,
}: ProductTypeFormProps) {
  const queryClient = useQueryClient();
  const isEdit = !!propId;

  // Load existing types for auto-suggestions
  const { data: existingTypes = [] } = useQuery({
    queryKey: ['config', 'productTypes'],
    queryFn: () => trpcClient.config.getProductTypes.query(),
  });

  const categorySuggestions = useMemo(() => {
    const cats = (existingTypes as any[]).map((t: any) => t.category).filter(Boolean);
    return Array.from(new Set(cats)) as string[];
  }, [existingTypes]);

  const groupSuggestions = useMemo(() => {
    const groups = (existingTypes as any[]).map((t: any) => t.group).filter(Boolean);
    return Array.from(new Set(groups)) as string[];
  }, [existingTypes]);

  const [formData, setFormData] = useState<ProductTypeFormData>({
    code: propInitialData?.code || propInitialData?.name || '',
    category: propInitialData?.category || lockedFields.category || '',
    group: propInitialData?.group || lockedFields.group || '',
    unit: propInitialData?.unit || lockedFields.unit || 'Cái',
    standardProcess: propInitialData?.standardProcess || '',
    productionDays: propInitialData?.productionDays !== undefined ? Number(propInitialData.productionDays) : 3,
    profitMargin: propInitialData?.profitMargin || '20%',
  });

  const isManualEditedRef = React.useRef(Boolean(propInitialData?.code));

  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleCategoryChange = (newCat: string) => {
    setFormData((prev) => {
      const updated = { ...prev, category: newCat };
      if (!isEdit && !isManualEditedRef.current) {
        updated.code = generateProductTypeCode({ category: newCat, group: prev.group });
      }
      return updated;
    });
  };

  const handleGroupChange = (newGroup: string) => {
    setFormData((prev) => {
      const updated = { ...prev, group: newGroup };
      if (!isEdit && !isManualEditedRef.current) {
        updated.code = generateProductTypeCode({ category: prev.category, group: newGroup });
      }
      return updated;
    });
  };

  useEffect(() => {
    if (propInitialData) {
      isManualEditedRef.current = Boolean(propInitialData.code);
      setFormData({
        code: propInitialData.code || propInitialData.name || '',
        category: propInitialData.category || lockedFields.category || '',
        group: propInitialData.group || lockedFields.group || '',
        unit: propInitialData.unit || lockedFields.unit || 'Cái',
        standardProcess: propInitialData.standardProcess || '',
        productionDays: propInitialData.productionDays !== undefined ? Number(propInitialData.productionDays) : 3,
        profitMargin: propInitialData.profitMargin || '20%',
      });
    }
  }, [propInitialData]);

  const saveMutation = useMutation({
    mutationFn: async (payload: ProductTypeFormData) => {
      if (isEdit && propId) {
        return await trpcClient.config.updateProductType.mutate({
          id: propId,
          data: {
            code: payload.code.trim(),
            category: payload.category?.trim() ?? '',
            group: payload.group?.trim() ?? '',
            unit: payload.unit?.trim() ?? '',
            standardProcess: payload.standardProcess?.trim() ?? '',
            productionDays: payload.productionDays !== undefined ? Number(payload.productionDays) : undefined,
            profitMargin: payload.profitMargin?.trim() ?? '',
          },
        });
      }
      return await trpcClient.config.addProductType.mutate({
        code: payload.code.trim(),
        category: payload.category?.trim() || undefined,
        group: payload.group?.trim() || undefined,
        unit: payload.unit?.trim() || undefined,
        standardProcess: payload.standardProcess?.trim() || undefined,
        productionDays: payload.productionDays !== undefined ? Number(payload.productionDays) : undefined,
        profitMargin: payload.profitMargin?.trim() || undefined,
      } as any);
    },
    onSuccess: async (res: any) => {
      // 1. Optimistic cache update (0ms) so new/updated item appears at the top of Page 1 immediately
      if (res) {
        queryClient.setQueryData(['product-types'], (old: any[] | undefined) => {
          const list = Array.isArray(old) ? old : [];
          return [res, ...list.filter((item: any) => item.id !== res.id)];
        });
      }

      // 2. Show Vietnamese toast notification immediately
      toast.success(isEdit ? `Đã cập nhật loại sản phẩm thành công: ${formData.code}` : `Đã thêm loại sản phẩm: ${formData.code}`);

      // 3. Synchronize all dependent queries
      await Promise.all([
        queryClient.refetchQueries({ queryKey: ['product-types'] }),
        queryClient.invalidateQueries({ queryKey: ['product-types'] }),
        queryClient.invalidateQueries({ queryKey: ['product-types-lookup'] }),
        queryClient.invalidateQueries({ queryKey: ['config', 'productTypes'] }),
        queryClient.invalidateQueries({ queryKey: ['combobox'] }),
      ]);

      // 4. Notify caller after fresh data is synchronized
      if (onSuccess && res) {
        onSuccess({
          id: res.id,
          label: res.code || formData.code,
          code: res.code || formData.code,
          original: res,
        });
      }
    },
    onError: (err: any) => {
      const rawMsg = err?.message || '';
      let cleanMsg = rawMsg;
      if (rawMsg.includes('Unique constraint failed') || rawMsg.includes('P2002') || rawMsg.includes('đã tồn tại')) {
        cleanMsg = `Mã loại sản phẩm '${formData.code}' đã tồn tại trong hệ thống. Vui lòng nhập mã khác.`;
      }
      toast.error(cleanMsg || `Lỗi ${isEdit ? 'cập nhật' : 'tạo'} loại sản phẩm`);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const result = productTypeSchema.safeParse(formData);
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.errors.forEach((err) => {
        if (err.path[0]) {
          fieldErrors[err.path[0].toString()] = err.message;
        }
      });
      setErrors(fieldErrors);
      const firstErr = result.error.errors[0]?.message;
      if (firstErr) {
        toast.error(firstErr);
      }
      return;
    }

    // Kiểm tra trùng mã trên danh sách đang hoạt động trước khi gửi lên server
    const cachedList = (queryClient.getQueryData(['product-types']) as any[]) || [];
    const combinedList = [...cachedList, ...((existingTypes as any[]) || [])];
    const codeNorm = result.data.code.trim().toLowerCase();
    const isDuplicate = combinedList.some(
      (item: any) =>
        item &&
        !item.isDeleted &&
        String(item.code || '').trim().toLowerCase() === codeNorm &&
        (!isEdit || item.id !== propId)
    );
    if (isDuplicate) {
      const dupMsg = `Mã loại sản phẩm '${result.data.code.trim()}' đã tồn tại trong hệ thống. Vui lòng chọn hoặc nhập mã khác.`;
      setErrors({ code: dupMsg });
      toast.error(dupMsg);
      return;
    }

    setErrors({});
    saveMutation.mutate(result.data);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Code / Tên loại sản phẩm */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <Label htmlFor="pt-code" className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
            <Tags className="h-3.5 w-3.5 text-indigo-500" />
            Mã loại sản phẩm <span className="text-rose-500">*</span>
          </Label>
          <span
            className={`text-[10px] flex items-center gap-1 ${
              formData.code.length > 50 ? 'text-rose-600 font-bold' : 'text-slate-400'
            }`}
          >
            <Sparkles className="h-3 w-3 text-amber-500" /> {formData.code.length}/50 ký tự (tối đa 50 ký tự)
          </span>
        </div>
        <Input
          id="pt-code"
          autoFocus={!isEdit}
          placeholder="VD: HOP-CAR, TUI-GIAY..."
          value={formData.code}
          onChange={(e) => {
            const val = e.target.value.toUpperCase();
            isManualEditedRef.current = val.trim().length > 0;
            setFormData((prev) => ({ ...prev, code: val }));
            if (val.length > 50) {
              setErrors((prev) => ({
                ...prev,
                code: 'Vui lòng nhập dưới 50 ký tự (Mã loại sản phẩm không được vượt quá 50 ký tự)',
              }));
            } else if (errors.code) {
              setErrors((prev) => {
                const next = { ...prev };
                delete next.code;
                return next;
              });
            }
          }}
          className={`h-8 text-xs font-mono font-bold uppercase tracking-wider ${
            errors.code || formData.code.length > 50
              ? 'border-rose-500 focus-visible:ring-rose-500 text-rose-700'
              : 'focus-visible:ring-indigo-500'
          }`}
          disabled={lockedFields.code !== undefined}
        />
        {errors.code && (
          <p data-testid="pt-code-error" className="text-[11px] font-medium text-rose-600">
            {errors.code}
          </p>
        )}
      </div>

      {/* Grid: Danh mục & Nhóm */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="pt-category" className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
            <Layers className="h-3.5 w-3.5 text-slate-500" />
            Danh mục sản phẩm
          </Label>
          <Input
            id="pt-category"
            list="pt-category-list"
            placeholder="VD: Hộp Carton Bồi, Túi Giấy..."
            value={formData.category}
            onChange={(e) => handleCategoryChange(e.target.value)}
            className="h-8 text-xs"
            disabled={lockedFields.category !== undefined}
          />
          <datalist id="pt-category-list">
            {categorySuggestions.map((cat) => (
              <option key={cat} value={cat} />
            ))}
          </datalist>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="pt-group" className="text-xs font-semibold text-slate-700">
            Nhóm sản phẩm
          </Label>
          <Input
            id="pt-group"
            list="pt-group-list"
            placeholder="VD: Hộp cứng, Thùng carton..."
            value={formData.group}
            onChange={(e) => handleGroupChange(e.target.value)}
            className="h-8 text-xs"
            disabled={lockedFields.group !== undefined}
          />
          <datalist id="pt-group-list">
            {groupSuggestions.map((grp) => (
              <option key={grp} value={grp} />
            ))}
          </datalist>
        </div>
      </div>

      {/* Grid: ĐVT, Thời gian SX chuẩn, Biên lợi nhuận */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="pt-unit" className="text-xs font-semibold text-slate-700">
            Đơn vị tính (ĐVT)
          </Label>
          <Input
            id="pt-unit"
            list="pt-unit-list"
            placeholder="Cái, Hộp, Chiếc..."
            value={formData.unit}
            onChange={(e) => setFormData((prev) => ({ ...prev, unit: e.target.value }))}
            className="h-8 text-xs"
            disabled={lockedFields.unit !== undefined}
          />
          <datalist id="pt-unit-list">
            <option value="Cái" />
            <option value="Hộp" />
            <option value="Bộ" />
            <option value="Chiếc" />
            <option value="Cuộn" />
            <option value="Kg" />
            <option value="Tấm" />
          </datalist>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="pt-productionDays" className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
            <Clock className="h-3.5 w-3.5 text-amber-500" />
            Thời gian SX chuẩn (ngày)
          </Label>
          <Input
            id="pt-productionDays"
            type="number"
            min={0}
            placeholder="3"
            value={formData.productionDays ?? ''}
            onChange={(e) => setFormData((prev) => ({ ...prev, productionDays: e.target.value ? Number(e.target.value) : undefined }))}
            className="h-8 text-xs"
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="pt-profitMargin" className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
            <TrendingUp className="h-3.5 w-3.5 text-emerald-500" />
            Biên lợi nhuận định mức
          </Label>
          <Input
            id="pt-profitMargin"
            placeholder="VD: 20%, 25%..."
            value={formData.profitMargin}
            onChange={(e) => setFormData((prev) => ({ ...prev, profitMargin: e.target.value }))}
            className="h-8 text-xs"
          />
        </div>
      </div>

      {/* Quy trình sản xuất chuẩn */}
      <div className="space-y-1.5">
        <Label htmlFor="pt-standardProcess" className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
          <Settings className="h-3.5 w-3.5 text-slate-500" />
          Quy trình sản xuất chuẩn (Standard Process)
        </Label>
        <Input
          id="pt-standardProcess"
          placeholder="VD: In Offset -> Cán màng nhiệt -> Ép kim -> Bế decal -> Dán đáy"
          value={formData.standardProcess}
          onChange={(e) => setFormData((prev) => ({ ...prev, standardProcess: e.target.value }))}
          className="h-8 text-xs"
        />
        <p className="text-[10px] text-slate-400">
          Quy trình tiêu chuẩn sẽ tự động định hình các công đoạn gợi ý khi lập lệnh sản xuất.
        </p>
      </div>

      {/* Footer Buttons */}
      <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100">
        {onCancel && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onCancel}
            disabled={saveMutation.isPending}
            className="h-8 text-xs px-3"
          >
            Hủy
          </Button>
        )}
        <Button
          type="submit"
          size="sm"
          disabled={saveMutation.isPending}
          className="h-8 text-xs px-4 bg-indigo-600 hover:bg-indigo-700 text-white font-medium"
        >
          {saveMutation.isPending && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
          {submitLabel || (isEdit ? 'Lưu thay đổi' : 'Tạo loại sản phẩm')}
        </Button>
      </div>
    </form>
  );
}
